import { useQuery } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { ProfileMatch } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface SearchFacets {
  [key: string]: {
    [value: string]: number
  }
}

/**
 * Highlight result structure from Algolia search
 */
interface HighlightResult {
  value: string
  match_level: string
  matched_words: string[]
  fully_highlighted?: boolean
}

/**
 * Keyword highlight structure from Algolia search
 */
interface KeywordHighlight {
  value: string
  matchLevel: string
  matchedWords: string[]
}

/**
 * Profile structure directly from Algolia search response
 */
export interface AlgoliaProfile {
  object_id: string
  id: string
  highlight_result?: {
    firstName?: HighlightResult
    lastName?: HighlightResult
    jobField?: HighlightResult
    companyName?: HighlightResult
    schoolName?: HighlightResult
    districtName?: HighlightResult
    keywords?: KeywordHighlight[]
    cities?: KeywordHighlight[]
    [key: string]: any
  }
  firstName: string
  lastName: string
  avatarUrl?: string
  email?: string
  emailAddress?: string
  phone?: string
  color?: string
  status?: string

  // Role-specific fields
  role?: string
  companyName?: string // For professionals
  jobField?: string // For professionals
  schoolName?: string // For educators
  districtName?: string // For educators
  districtId?: string // For educators
  schoolId?: string // For educators

  // Common fields
  about?: string
  bio?: string
  keywords?: string[]
  secondaryLanguages?: string[]
  otherSecondaryLanguages?: string[]
  gradeLevels?: string[]
  cities?: string[]
  contentAreas?: string[]
  connectionPreference?: string
  preferredActivities?: string[]

  // Timestamps
  createdAt?: string
  updatedAt?: string
  approvedAt?: string
  approvedBy?: string
  deniedOn?: string
  deniedBy?: string

  // Other fields
  unavailable?: boolean
  sortKey?: string
  jobFields?: string[]
}

/**
 * Response structure from the profile search API
 */
export interface ProfileSearchResponse {
  items: AlgoliaProfile[]
  total: number
  page: number
  pages: number
  limit: number
  facets: SearchFacets
  facetsStats: Record<string, any> | null
  exhaustiveFacetsCount: boolean
  exhaustiveNbHits: boolean
  processingTimeMS: number
}

/**
 * Parameters for profile search
 */
export interface ProfileSearchParams {
  query?: string
  facet_filters?: string[]
  filters?: string
  page?: number
  limit?: number
  adminSearch?: boolean
  sort_by?: string

  // Legacy API parameters (for backward compatibility)
  role?: string
  keywords?: string[]
  content_areas?: string[]
  grade_levels?: string[]
}

/**
 * Convert an Algolia profile to the application's ProfileMatch format
 */
const convertToProfileMatch = (profile: AlgoliaProfile): ProfileMatch => {
  return {
    id: profile.id,
    name: `${profile.firstName} ${profile.lastName}`,
    color: profile.color || '',
    avatarUrl: profile.avatarUrl || '',
    title: profile.role === 'EDUCATOR' ? profile.districtName || '' : '',
    jobField: profile.jobField || '',
    companyName: profile.companyName || profile.schoolName || '',
    about: profile.about || profile.bio || '',
    publicBio: profile.bio || '',
    keywords: profile.keywords || [],
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    // Add any other fields needed by your app
  }
}

/**
 * Hook for searching profiles with various filters and pagination
 */
export const useProfileSearch = (params: ProfileSearchParams = {}) => {
  const fetchWithAuth = useFetchWithAuth()

  // Set default values
  const {
    query = '',
    facet_filters,
    filters,
    page = 0,
    limit = 12,
    role,
    keywords,
    content_areas,
    grade_levels,
  } = params

  return useQuery<ProfileSearchResponse>({
    queryKey: [
      'profileSearch',
      query,
      facet_filters,
      filters,
      page,
      limit,
      role,
      keywords,
      content_areas,
      grade_levels,
      params.adminSearch,
      params.sort_by,
    ],
    queryFn: async () => {
      // Build query params
      const queryParams = new URLSearchParams()

      if (query) queryParams.append('query', query)
      if (filters) queryParams.append('filters', filters)
      if (page !== undefined) queryParams.append('page', page.toString())
      if (limit !== undefined) queryParams.append('limit', limit.toString())
      if (params.adminSearch) queryParams.append('admin_search', 'true')
      if (params.sort_by) queryParams.append('sort_by', params.sort_by)

      // Add facet filters if present
      if (Array.isArray(facet_filters) && facet_filters.length > 0) {
        console.log('Adding facet filters to request:', facet_filters)
        facet_filters.forEach((filter) => {
          queryParams.append('facet_filters', filter)
        })
      } else {
        console.log('No facet filters to add:', { facet_filters })
      }

      // Add legacy parameters if present
      if (role) queryParams.append('role', role)

      if (Array.isArray(keywords) && keywords.length > 0) {
        keywords.forEach((keyword) => {
          queryParams.append('keywords', keyword)
        })
      }

      if (Array.isArray(content_areas) && content_areas.length > 0) {
        content_areas.forEach((area) => {
          queryParams.append('content_areas', area)
        })
      }

      if (Array.isArray(grade_levels) && grade_levels.length > 0) {
        grade_levels.forEach((level) => {
          queryParams.append('grade_levels', level)
        })
      }

      // Construct the request URL
      const requestUrl = `${API_URL}/profiles/search?${queryParams.toString()}`

      // Log the full request URL for debugging
      console.log('Profile search request URL:', requestUrl)

      // Fetch profiles from API with filters
      const response = await fetchWithAuth(requestUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      return response as ProfileSearchResponse
    },
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
  })
}

/**
 * Hook for searching profiles with conversion to the application's ProfileMatch format
 */
export const useProfileMatchSearch = (params: ProfileSearchParams = {}) => {
  const result = useProfileSearch(params)

  // Transform the data to ProfileMatch format if available
  const transformedData = result.data
    ? {
        ...result.data,
        items: result.data.items.map(convertToProfileMatch),
      }
    : undefined

  return {
    ...result,
    data: transformedData,
  }
}

/**
 * Hook for simple keyword search of profiles
 * A convenience wrapper around useProfileMatchSearch for simple use cases
 */
export const useKeywordProfileSearch = (
  keyword: string,
  limit: number = 12
) => {
  return useProfileMatchSearch({
    query: keyword,
    limit,
  })
}

/**
 * Hook for finding profiles by role and optional filters
 * A convenience wrapper around useProfileMatchSearch for common role-based queries
 */
export const useRoleProfileSearch = (
  role: 'educator' | 'professional',
  filters?: {
    keywords?: string[]
    content_areas?: string[]
    grade_levels?: string[]
  },
  limit: number = 12
) => {
  return useProfileMatchSearch({
    role,
    keywords: filters?.keywords,
    content_areas: filters?.content_areas,
    grade_levels: filters?.grade_levels,
    limit,
  })
}
